import React, { useEffect, useRef } from 'react';
import './Home.css';

/* ─── Section: Hero ────────────────────────────────────────────────────── */
function Hero() {
  return (
    <section className="hero">
      {/* Background layers */}
      <div className="hero-bg">
        <div className="hero-blob hero-blob-1" />
        <div className="hero-blob hero-blob-2" />
        <div className="hero-grid" />
      </div>

      <div className="container hero-content">
        <div className="hero-badge">
          <span className="hero-badge-dot" />
          Now available on Google Play
        </div>

        <h1 className="hero-title">
          Walk More.<br />
          <span className="gradient-text">Earn DEEP</span><br />
          Points.
        </h1>

        <p className="hero-sub">
          Every step you take earns DEEP Points. Complete daily tasks,
          invite friends, and watch your rewards grow — all while getting healthier.
        </p>

        <div className="hero-actions">
          <a
            href="https://play.google.com/store"
            target="_blank"
            rel="noreferrer"
            className="btn-primary hero-btn"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3.18 23.5c.28.16.6.24.94.22l.09-.05 11.16-6.44-2.42-2.42L3.18 23.5zm15.9-10.28L16.7 11.8l-2.66 2.66 2.65 2.65 2.4-1.39c.69-.4.69-1.06 0-1.5zM3.09.5C2.8.68 2.62 1 2.62 1.44v21.12c0 .44.18.76.47.94l.09.05 11.8-11.8L3.09.5zm10.33 10.96L4.06.8l9.71 5.6-2.42 2.42 2.07 2.64z"/>
            </svg>
            Download Free on Android
          </a>
          <a href="#how" className="btn-outline">
            How it works
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </a>
        </div>

        <div className="hero-stats">
          {[
            { val: '10K+', label: 'Active Users' },
            { val: '500M+', label: 'Steps Tracked' },
            { val: '4.8★', label: 'App Rating' },
          ].map(s => (
            <div key={s.val} className="hero-stat">
              <span className="hero-stat-val">{s.val}</span>
              <span className="hero-stat-label">{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Floating App Mockup */}
      <div className="hero-visual">
        <div className="phone-mockup float">
          <div className="phone-screen">
            <div className="phone-screen-inner">
              <div className="mock-top-bar">
                <span className="mock-greeting">Good morning 👋</span>
                <div className="mock-pts">
                  <span className="mock-pts-val">2,450</span>
                  <span className="mock-pts-lbl">DEEP pts</span>
                </div>
              </div>
              <div className="mock-ring-wrap">
                <svg className="mock-ring" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="40" fill="none" stroke="rgba(157,78,221,0.15)" strokeWidth="8"/>
                  <circle cx="50" cy="50" r="40" fill="none" stroke="url(#ringGrad)" strokeWidth="8"
                    strokeLinecap="round" strokeDasharray="180 251" strokeDashoffset="-62" transform="rotate(-90 50 50)"/>
                  <defs>
                    <linearGradient id="ringGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#9D4EDD"/>
                      <stop offset="100%" stopColor="#FBBF24"/>
                    </linearGradient>
                  </defs>
                </svg>
                <div className="mock-ring-center">
                  <span className="mock-steps">7,240</span>
                  <span className="mock-steps-lbl">steps today</span>
                </div>
              </div>
              <div className="mock-cards">
                {[
                  { icon: '🔥', label: 'Calories', val: '312 kcal' },
                  { icon: '📍', label: 'Distance', val: '5.4 km' },
                ].map(c => (
                  <div key={c.label} className="mock-card">
                    <span>{c.icon}</span>
                    <div>
                      <p className="mock-card-val">{c.val}</p>
                      <p className="mock-card-lbl">{c.label}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        {/* Floating pills */}
        <div className="hero-pill hero-pill-1">+15 pts 📺 Video</div>
        <div className="hero-pill hero-pill-2">🔥 7-day streak!</div>
        <div className="hero-pill hero-pill-3">+500 pts unlocked</div>
      </div>
    </section>
  );
}

/* ─── Section: Marquee ──────────────────────────────────────────────────── */
const MARQUEE_ITEMS = ['Walk & Earn', '$DEEP Points', 'Step Tracker', 'Daily Rewards', 'Friend Invites', 'Streak Bonuses', 'Health Goals', 'Crypto Rewards'];

function Marquee() {
  const items = [...MARQUEE_ITEMS, ...MARQUEE_ITEMS];
  return (
    <div className="marquee-wrap">
      <div className="marquee-track">
        {items.map((item, i) => (
          <span key={i} className="marquee-item">
            <span className="marquee-dot" />
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ─── Section: Features ─────────────────────────────────────────────────── */
const FEATURES = [
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="28" height="28">
        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    color: '#FBBF24',
    title: 'Live Step Tracking',
    desc: 'Real-time accelerometer tracking with GPS-enhanced distance calculation. Watch your step count update live — even in your notification bar.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="28" height="28">
        <circle cx="12" cy="12" r="10"/>
        <path d="M12 6v6l4 2" strokeLinecap="round"/>
      </svg>
    ),
    color: '#9D4EDD',
    title: 'Daily Task Rewards',
    desc: 'Watch 5 daily videos, follow social channels, download partner apps and complete your task list to earn bonus DEEP Points every single day.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="28" height="28">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" strokeLinecap="round"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" strokeLinecap="round"/>
      </svg>
    ),
    color: '#34D399',
    title: 'Invite & Earn',
    desc: 'Refer friends and earn milestone bonuses when they join. 3 friends = 100 pts, 5 friends = 200 pts, 7 friends = 350 pts — and rising.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="28" height="28">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
      </svg>
    ),
    color: '#F87171',
    title: 'Daily Streaks',
    desc: 'Build a daily streak and unlock massive milestone bonuses — 500 pts at 7 days, 1,000 pts at 14 days, and 2,500 pts at 30 days.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="28" height="28">
        <rect x="3" y="3" width="18" height="18" rx="4"/>
        <path d="M3 9h18M9 21V9" strokeLinecap="round"/>
      </svg>
    ),
    color: '#38BDF8',
    title: 'Analytics Dashboard',
    desc: 'Track your health journey with detailed charts — daily steps, calories burned, distance covered, and active minutes over time.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="28" height="28">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    color: '#C77DFF',
    title: 'Secure & Private',
    desc: 'Your data is encrypted end-to-end via Supabase enterprise infrastructure. GPS only runs when you actively track — never in the background.',
  },
];

function Features() {
  return (
    <section className="section features" id="features">
      <div className="container">
        <div className="section-header center">
          <div className="section-label">✦ App Features</div>
          <h2 className="section-title">Everything you need to<br /><span className="gradient-text">earn while you move</span></h2>
          <p className="section-sub" style={{ margin: '0 auto' }}>
            A complete health and rewards platform built around your daily movement.
          </p>
        </div>
        <div className="features-grid">
          {FEATURES.map((f, i) => (
            <div key={i} className="feature-card glow-card" style={{ animationDelay: `${i * 0.08}s` }}>
              <div className="feature-icon" style={{ background: `${f.color}18`, border: `1.5px solid ${f.color}35`, color: f.color }}>
                {f.icon}
              </div>
              <h3 className="feature-title">{f.title}</h3>
              <p className="feature-desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Section: How It Works ─────────────────────────────────────────────── */
const HOW_STEPS = [
  { n: '01', title: 'Download the App', desc: 'Free on Android. Sign up in under 30 seconds. No credit card ever required.', color: '#9D4EDD' },
  { n: '02', title: 'Start Walking', desc: 'Tap "Start Walking" and the app tracks every step using your phone\'s sensors with anti-cheat protection.', color: '#FBBF24' },
  { n: '03', title: 'Earn DEEP Points', desc: 'Every 1,000 steps earns points. Complete daily tasks and maintain streaks for bonus rewards.', color: '#34D399' },
  { n: '04', title: 'Watch Rewards Grow', desc: 'DEEP Points accumulate and will convert to $DEEP tokens at launch. Early adopters earn the most.', color: '#F87171' },
];

function HowItWorks() {
  return (
    <section className="section how" id="how">
      <div className="how-bg" />
      <div className="container">
        <div className="section-header">
          <div className="section-label">✦ How It Works</div>
          <h2 className="section-title">Simple as taking<br /><span className="gradient-text">a walk</span></h2>
          <p className="section-sub">Four steps from download to earning. No complicated setup.</p>
        </div>
        <div className="how-steps">
          {HOW_STEPS.map((s, i) => (
            <div key={i} className="how-step">
              <div className="how-step-num" style={{ color: s.color, borderColor: `${s.color}40`, background: `${s.color}10` }}>{s.n}</div>
              <div className="how-step-connector" style={{ background: `linear-gradient(180deg, ${s.color}60, transparent)` }} />
              <div className="how-step-body">
                <h3 style={{ color: '#fff', fontFamily: 'Syne, sans-serif', fontSize: 20, fontWeight: 700, marginBottom: 10 }}>{s.title}</h3>
                <p style={{ color: 'rgba(255,255,255,0.50)', fontSize: 15, lineHeight: 1.7 }}>{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Section: Rewards ──────────────────────────────────────────────────── */
const REWARD_CARDS = [
  { title: 'Step Milestones', items: ['1,000 steps → +10 pts', '5,000 steps → +50 pts', '10,000 steps → +100 pts', '20,000 steps → +200 pts'], color: '#FBBF24', icon: '👟' },
  { title: 'Daily Tasks', items: ['Watch 5 videos → +75 pts', 'Follow Instagram → +50 pts', 'Join Telegram → +50 pts', 'Download task → +50 pts', 'All tasks done → +200 pts'], color: '#9D4EDD', icon: '✅' },
  { title: 'Streak Bonuses', items: ['7-day streak → +500 pts', '14-day streak → +1,000 pts', '30-day streak → +2,500 pts'], color: '#34D399', icon: '🔥' },
  { title: 'Social Rewards', items: ['Invite 3 friends → +100 pts', 'Invite 5 friends → +200 pts', 'Invite 7 friends → +350 pts'], color: '#F87171', icon: '👥' },
];

function Rewards() {
  return (
    <section className="section rewards" id="rewards">
      <div className="container">
        <div className="section-header center">
          <div className="section-label">✦ Rewards System</div>
          <h2 className="section-title">Every action<br /><span className="gradient-text">earns you more</span></h2>
          <p className="section-sub" style={{ margin: '0 auto' }}>Multiple ways to earn DEEP Points every single day.</p>
        </div>
        <div className="rewards-grid">
          {REWARD_CARDS.map((r, i) => (
            <div key={i} className="reward-card glow-card">
              <div className="reward-card-header">
                <span className="reward-icon">{r.icon}</span>
                <h3 style={{ fontFamily: 'Syne, sans-serif', fontSize: 18, fontWeight: 700, color: r.color }}>{r.title}</h3>
              </div>
              <ul className="reward-list">
                {r.items.map((item, j) => (
                  <li key={j}>
                    <span className="reward-bullet" style={{ background: r.color }} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* DEEP Points disclaimer */}
        <div className="reward-disclaimer">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FBBF24" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/>
          </svg>
          <p><strong>Important:</strong> DEEP Points are virtual in-app rewards with no guaranteed monetary value. They do not represent ownership of any financial asset. We do not charge users to earn points, and no financial return is promised or guaranteed.</p>
        </div>
      </div>
    </section>
  );
}

/* ─── Section: CTA ──────────────────────────────────────────────────────── */
function CTA() {
  return (
    <section className="section cta-section">
      <div className="container">
        <div className="cta-card">
          <div className="cta-glow" />
          <div className="cta-content">
            <img src="/logo.png" alt="DEEP Health" className="cta-logo" />
            <h2 className="section-title">Ready to start<br /><span className="gradient-text">earning while walking?</span></h2>
            <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 16, marginBottom: 36, maxWidth: 420, lineHeight: 1.7 }}>
              Join thousands of users already earning DEEP Points. Free forever — no hidden fees, no subscriptions.
            </p>
            <a
              href="https://play.google.com/store"
              target="_blank"
              rel="noreferrer"
              className="btn-primary"
              style={{ fontSize: 16, padding: '16px 40px' }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3.18 23.5c.28.16.6.24.94.22l.09-.05 11.16-6.44-2.42-2.42L3.18 23.5zm15.9-10.28L16.7 11.8l-2.66 2.66 2.65 2.65 2.4-1.39c.69-.4.69-1.06 0-1.5zM3.09.5C2.8.68 2.62 1 2.62 1.44v21.12c0 .44.18.76.47.94l.09.05 11.8-11.8L3.09.5zm10.33 10.96L4.06.8l9.71 5.6-2.42 2.42 2.07 2.64z"/>
              </svg>
              Get it on Google Play — Free
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Main Export ───────────────────────────────────────────────────────── */
export default function Home() {
  return (
    <main className="home">
      <Hero />
      <Marquee />
      <Features />
      <HowItWorks />
      <Rewards />
      <CTA />
    </main>
  );
}
